import { useEffect, useMemo, useState } from 'react'
import { History, Search, Sparkles, Wrench, CheckCircle2, CalendarDays, X } from 'lucide-react'
import { HISTORICO, contarPorTipo, dataBR, filtrarHistorico } from '../lib/historicoVersoes'

// Histórico de atualizações: abre ao clicar na versão, no rodapé do menu (padrão Vencedores).
const TIPOS = {
  novo: { rotulo: 'Novo', classe: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icone: Sparkles },
  melhoria: { rotulo: 'Melhoria', classe: 'bg-sky-50 text-sky-700 border-sky-200', Icone: CheckCircle2 },
  correcao: { rotulo: 'Correção', classe: 'bg-amber-50 text-amber-700 border-amber-200', Icone: Wrench },
}
const FILTROS = [['all', 'Tudo'], ['novo', 'Novidades'], ['melhoria', 'Melhorias'], ['correcao', 'Correções']]
const SEMANA = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado']
function diaDaSemana(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''))
  return m ? SEMANA[new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay()] : ''
}

export default function HistoricoVersoesModal({ open, onClose, versaoAtual }) {
  const [busca, setBusca] = useState('')
  const [tipo, setTipo] = useState('all')
  useEffect(() => { if (!open) { setBusca(''); setTipo('all') } }, [open])
  const lista = useMemo(() => filtrarHistorico(HISTORICO, busca, tipo), [busca, tipo])
  const contagem = useMemo(() => contarPorTipo(HISTORICO), [])
  if (!open) return null

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading font-extrabold text-graphite text-lg">Histórico de atualizações</h3>
            <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5">
              <History size={14} />
              {versaoAtual ? <>Versão no ar: <b className="text-gray-700">{versaoAtual}</b> · </> : null}
              o que mudou no sistema em cada atualização
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>

        <div className="px-5 pt-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar no histórico (ex.: telecom, PIX, comissão)"
              className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-accent-400" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTROS.map(([k, r]) => (
              <button key={k} type="button" onClick={() => setTipo(k)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${tipo === k ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-gray-600 border-gray-200 hover:border-accent-400'}`}>
                {r} <span className="opacity-70">{contagem[k] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {lista.length === 0 && <p className="text-sm text-gray-500 py-6 text-center">Nada encontrado para o que você procurou.</p>}
          {lista.map((entrada) => (
            <div key={entrada.data}>
              <div className="flex items-center gap-2 mb-1">
                <CalendarDays size={14} className="text-accent-500" />
                <span className="font-bold text-gray-800 tabular-nums">{dataBR(entrada.data)}</span>
                <span className="text-xs text-gray-400">{diaDaSemana(entrada.data)}</span>
              </div>
              {entrada.titulo && <p className="text-sm font-semibold text-gray-700 mb-2">{entrada.titulo}</p>}
              <ul className="space-y-2">
                {entrada.itens.map((item, i) => {
                  const t = TIPOS[item.tipo] || TIPOS.melhoria
                  const Icone = t.Icone
                  return (
                    <li key={i} className="flex gap-2.5">
                      <span className={`shrink-0 inline-flex items-center gap-1 h-fit px-2 py-0.5 rounded-full border text-[10.5px] font-bold ${t.classe}`}><Icone size={11} /> {t.rotulo}</span>
                      <div className="min-w-0">
                        {item.modulo && <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{item.modulo}</span>}
                        <p className="text-sm text-gray-700 leading-snug">{item.texto}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
